import ArrSVG from '@public/icons/arr.svg';
import ArrowSVG from '@public/icons/arrow.svg';
import classNames from 'classnames';
import { AnimatePresence, m as motion } from 'framer-motion';
import isequal from 'lodash.isequal';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';
import { usePavilionsValue } from '@/shared/atoms/pavilionsData';
import ConstructorButton from '@/shared/components/configurator/ConstructorButton';
import { useBannerVisibleSetState } from '@/shared/components/configurator/atoms/banners-visible';
import {
    useConfiguratorOpenedCameraPositionValue,
    useDefaultCameraPositionValue,
    useSetCameraPosition,
} from '@/shared/components/configurator/atoms/camera-position';
import { useSetConfiguratorStepState } from '@/shared/components/configurator/atoms/configurator-step-state';
import {
    useConfiguratorVisibleSetState,
    useConfiguratorVisibleValue,
} from '@/shared/components/configurator/atoms/configurator-visible';
import { useConfiguratorZoomedSetState } from '@/shared/components/configurator/atoms/configurator-zoomed';
import { useSetCurrentGroupPickerState } from '@/shared/components/configurator/atoms/current-group-picker-state';
import { useCurrentModelPropertiesValue } from '@/shared/components/configurator/atoms/current-model-properties';
import {
    BooleTypes,
    ExhibitTypes,
    FurnitureTypes,
    LightsTypes,
    PartitionTypes,
    ReceptionTypes,
    TVTypes,
    currentModelDefaultState,
    useCurrentModelStateValue,
    useSetCurrentModelState,
} from '@/shared/components/configurator/atoms/current-model-state';
import { useSetCurrentPickerState } from '@/shared/components/configurator/atoms/current-picker-state';
import { useHintVisibleSetState, useHintVisibleValue } from '@/shared/components/configurator/atoms/hint-visible';
import { customToCurrency } from '@/shared/lib/utils/to-currency';
import ConfiguratorCopyConfigButton from './ConfiguratorCopyConfigButton';

const Values = ({ values }: { values: { title: string; value: string } | { title: string; value: string }[] }) => {
    if (Array.isArray(values)) {
        return (
            <div>
                {values.map((item, i) => {
                    return (
                        <span key={i}>
                            {item.title}
                            {i !== values.length - 1 ? ', ' : ''}
                        </span>
                    );
                })}
            </div>
        );
    }
    return <div>{values.title}</div>;
};

type Props = {
    slug: string;
};
const ConfiguratorHero = ({ slug }: Props) => {
    const { pavilionData, pavilionDataLoading, pavilionDataError } = usePavilionsValue();
    const pavilionsData = pavilionData.data;

    const setStep = useSetConfiguratorStepState();
    const properties = useCurrentModelPropertiesValue();
    const setPicker = useSetCurrentPickerState();
    const setGroupPicker = useSetCurrentGroupPickerState();
    const values = useCurrentModelStateValue();
    const setCameraPosition = useSetCameraPosition();
    const cameraDefaultPosition = useDefaultCameraPositionValue();
    const cameraConfigOpenedPosition = useConfiguratorOpenedCameraPositionValue();
    const [hasScroll, setHasScroll] = useState(false);
    const scrollingEl = useRef<HTMLDivElement>(null);
    const configuratorTop = useRef<HTMLDivElement>(null);
    const configuratorEl = useRef<HTMLDivElement>(null);
    const setCurrentModelState = useSetCurrentModelState();
    const setConfiguratorZoomed = useConfiguratorZoomedSetState();
    const isConfiguratorVisible = useConfiguratorVisibleValue();
    const setConfiguratorVisible = useConfiguratorVisibleSetState();
    const setHintVisible = useHintVisibleSetState();
    const hintVisible = useHintVisibleValue();
    const setBannersVisible = useBannerVisibleSetState();
    const [hiddenHeight, setHiddenHeight] = useState(0);

    const configLength = Object.keys(values).length;

    useIsomorphicLayoutEffect(() => {
        const el = scrollingEl.current;
        if (el) {
            if (el.scrollHeight > el.offsetHeight && !hasScroll) {
                setHasScroll(true);
            }

            if (el.scrollHeight === el.offsetHeight && hasScroll) {
                setHasScroll(false);
            }
        }
    }, [properties, hasScroll]);

    useIsomorphicLayoutEffect(() => {
        const topEl = configuratorTop.current;
        const el = configuratorEl.current;

        if (topEl && el) {
            const topHeight = topEl.offsetHeight;
            const computedStyles = getComputedStyle(el);
            const paddingTop = parseFloat(computedStyles.paddingTop);
            const paddingBottom = parseFloat(computedStyles.paddingBottom);
            setHiddenHeight(topHeight + paddingTop + paddingBottom);
        }
    }, []);

    useEffect(() => {
        if (isConfiguratorVisible) {
            setCameraPosition(cameraConfigOpenedPosition);
        } else {
            setCameraPosition(cameraDefaultPosition);
        }
    }, [isConfiguratorVisible, setCameraPosition, cameraDefaultPosition, cameraConfigOpenedPosition]);
    return (
        <div
            className={classNames('configurator', {
                'configurator--scroll': hasScroll,
                'configurator--hidden': !isConfiguratorVisible,
            })}
            ref={configuratorEl}
            style={{ '--hidden-height': `${hiddenHeight}px` } as CSSProperties}
        >
            <div className="configurator-body" ref={scrollingEl}>
                <div className="configurator-top" ref={configuratorTop}>
                    <ConfiguratorCopyConfigButton />
                    <div className="configurator-title text-l">Конфигурация</div>
                    {configLength > Object.keys(currentModelDefaultState).length && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="configurator-count text-xxs"
                        >
                            {configLength}
                        </motion.div>
                    )}
                    <button
                        className="configurator-toggler"
                        onClick={() => {
                            setConfiguratorVisible((prev) => !prev);
                        }}
                    >
                        <ArrSVG />
                    </button>
                </div>
                {pavilionsData && (
                    <AnimatePresence>
                        {isConfiguratorVisible && (
                            <>
                                <motion.ul
                                    initial={{
                                        opacity: 0,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        transition: {
                                            delay: 0.5,
                                            duration: 0.5,
                                        },
                                    }}
                                    exit={{
                                        opacity: 0,
                                        transition: {
                                            duration: 0.2,
                                        },
                                    }}
                                    className="config-list list-unstyled"
                                >
                                    {properties.map((item, i) => {
                                        if (item.type !== 'group') {
                                            const curValues = values[item.slug];

                                            return (
                                                <li key={i} className="config-list__item">
                                                    <button
                                                        className="config-list-item"
                                                        onClick={() => {
                                                            setConfiguratorZoomed(true);
                                                            if (hintVisible) {
                                                                setHintVisible(false);
                                                            }
                                                            if (item.type === 'color') {
                                                                setStep('color');
                                                            }
                                                            if (item.type === 'select') {
                                                                const property = item.property as {
                                                                    label: string;
                                                                    slug:
                                                                        | ReceptionTypes
                                                                        | LightsTypes
                                                                        | TVTypes
                                                                        | FurnitureTypes
                                                                        | ExhibitTypes
                                                                        | PartitionTypes;
                                                                    hint: string;
                                                                }[];
                                                                setStep('picker');
                                                                setPicker({
                                                                    title: item.title,
                                                                    slug: item.slug,
                                                                    property,
                                                                });
                                                            }

                                                            if (item.type === 'multiselect') {
                                                                const property = item.property as {
                                                                    label: string;
                                                                    slug: BooleTypes;
                                                                    hint: string;
                                                                }[];
                                                                setStep('multipicker');
                                                                setPicker({
                                                                    title: item.title,
                                                                    slug: item.slug,
                                                                    property,
                                                                });
                                                            }

                                                            if ('cameraPosition' in item && item.cameraPosition) {
                                                                setCameraPosition(item.cameraPosition);
                                                            }
                                                        }}
                                                    >
                                                        <div className="config-list-item__top">
                                                            <div className="config-list-item__title text-m">
                                                                {item.title}
                                                            </div>
                                                            <div className="config-list-item__arrow">
                                                                <ArrowSVG />
                                                            </div>
                                                        </div>
                                                        {curValues && (
                                                            <div className="config-list-item__hint text-xxs">
                                                                <Values
                                                                    values={
                                                                        curValues as
                                                                            | {
                                                                                  title: string;
                                                                                  value: string;
                                                                              }
                                                                            | {
                                                                                  title: string;
                                                                                  value: string;
                                                                              }[]
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                    </button>
                                                </li>
                                            );
                                        }
                                        const curValues = item.values
                                            .map((item) => values[item.slug]?.title)
                                            .filter(Boolean)
                                            .join(', ');

                                        return (
                                            <li key={i} className="config-list__item">
                                                <button
                                                    className="config-list-item"
                                                    onClick={() => {
                                                        if (hintVisible) {
                                                            setHintVisible(false);
                                                        }
                                                        setStep('group');
                                                        setGroupPicker({
                                                            title: item.title,
                                                            values: item.values,
                                                        });
                                                        if ('cameraPosition' in item && item.cameraPosition) {
                                                            setCameraPosition(item.cameraPosition);
                                                        }
                                                        setConfiguratorZoomed(true);
                                                    }}
                                                >
                                                    <div className="config-list-item__top">
                                                        <div className="config-list-item__title text-m">
                                                            {item.title}
                                                        </div>
                                                        <div className="config-list-item__arrow">
                                                            <ArrowSVG />
                                                        </div>
                                                    </div>
                                                    {curValues && (
                                                        <div className="config-list-item__hint text-xxs">
                                                            {curValues}
                                                        </div>
                                                    )}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </motion.ul>

                                <motion.div
                                    initial={{
                                        opacity: 0,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        transition: {
                                            delay: 0.5,
                                            duration: 0.5,
                                        },
                                    }}
                                    exit={{
                                        opacity: 0,
                                        transition: {
                                            duration: 0.2,
                                        },
                                    }}
                                    className="pavilion-info"
                                >
                                    <div className="text-l">Техническая информация</div>
                                    {pavilionsData[slug] ? (
                                        <div className="pavilion-props">
                                            <div className="pavilion-prop">
                                                <span className="pavilion-prop-key">Площадь</span>
                                                <span className="pavilion-prop-value">
                                                    <span className="text-s">
                                                        {`(${pavilionsData[slug].space}`} м<sup>2</sup>
                                                        {')'}
                                                    </span>
                                                </span>
                                            </div>
                                            {pavilionsData[slug].size && (
                                                <div className="pavilion-prop">
                                                    <span className="pavilion-prop-key">Размер (ШхДхВ)</span>
                                                    <span className="pavilion-prop-value">
                                                        <span className="text-s">{pavilionsData[slug].size} </span>
                                                    </span>
                                                </div>
                                            )}
                                            <div className="pavilion-prop">
                                                <span className="pavilion-prop-key">Размер баннеров</span>
                                                <span className="pavilion-prop-value">
                                                    <ConstructorButton
                                                        variant="blue"
                                                        geometryVariant="wide"
                                                        onClick={() => {
                                                            setStep('banners');
                                                            setBannersVisible(true);
                                                        }}
                                                    >
                                                        Показать
                                                    </ConstructorButton>
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div></div>
                                    )}
                                </motion.div>

                                <motion.div
                                    initial={{
                                        opacity: 0,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        transition: {
                                            delay: 0.6,
                                            duration: 0.5,
                                        },
                                    }}
                                    exit={{
                                        opacity: 0,
                                        transition: {
                                            duration: 0.2,
                                        },
                                    }}
                                    className="pavilion-info"
                                >
                                    <div className="text-l">Стоимость</div>
                                    {pavilionsData[slug] ? (
                                        <div className="pavilion-props">
                                            <div className="pavilion-prop">
                                                <span className="pavilion-prop-key">Цена от</span>
                                                <span className="pavilion-prop-value">
                                                    <span className="text-s">
                                                        {customToCurrency(pavilionsData[slug].price as number)} ₽/м
                                                        <sup>2</sup>
                                                    </span>
                                                </span>
                                            </div>
                                            <div className="pavilion-prop">
                                                <span className="pavilion-prop-key">Стоимость от</span>
                                                <span className="pavilion-prop-value">
                                                    <span className="text-s">
                                                        {customToCurrency(
                                                            (pavilionsData[slug].price as number) *
                                                                pavilionsData[slug].space,
                                                        )}{' '}
                                                        ₽
                                                    </span>
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div></div>
                                    )}
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>
                )}
            </div>
            <AnimatePresence>
                {isConfiguratorVisible && (
                    <motion.div
                        initial={{
                            opacity: 0,
                        }}
                        animate={{
                            opacity: 1,
                            transition: {
                                delay: 0.5,
                                duration: 0.5,
                            },
                        }}
                        exit={{
                            opacity: 0,
                            transition: {
                                duration: 0.2,
                            },
                        }}
                        className="configurator-hero-buttons"
                    >
                        {!isequal(values, currentModelDefaultState) && (
                            <ConstructorButton
                                variant="outline"
                                geometryVariant="wide"
                                onClick={() => {
                                    setCurrentModelState(currentModelDefaultState);
                                }}
                            >
                                Сбросить
                            </ConstructorButton>
                        )}
                        <ConstructorButton
                            variant="blue"
                            geometryVariant="wide"
                            onClick={() => {
                                setStep('form');
                            }}
                        >
                            Сохранить и продолжить
                        </ConstructorButton>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ConfiguratorHero;
